import React, { useState, useEffect } from 'react';
import { Form, Button, Tabs, Badge } from 'antd';
import { FormConfig } from '../DynamicForm';
import { FixedDeviceForm } from './';
import { YingshiIntegration, YingshiCamera } from '../YingshiCloud';

interface DeviceFormProps {
  form: any;
  initialValues: Record<string, any>;
  onFinish: (values: Record<string, any>) => void;
  onCancel: () => void;
  isEditing: boolean;
  formConfig: FormConfig | null;
  onDeviceTypeChange: (value: number) => void;
  editingDevice: any | null;
}

const DeviceForm: React.FC<DeviceFormProps> = ({
  form,
  initialValues,
  onFinish,
  onCancel,
  isEditing,
  formConfig,
  onDeviceTypeChange,
  editingDevice,
}) => {
  const [activeTabKey, setActiveTabKey] = useState<string>("basic");
  const [selectedCameras, setSelectedCameras] = useState<YingshiCamera[]>([]);
  
  // Set active tab based on device type and existing channels
  // Only run this effect when initialValues or editingDevice changes
  useEffect(() => {
    const deviceType = initialValues.deviceType;
    
    // If editing a video device with existing channels, switch to the Yingshi tab
    if (deviceType === 3 && editingDevice?.exData?.channel && Array.isArray(editingDevice.exData.channel) && editingDevice.exData.channel.length > 0) {
      setActiveTabKey("yingshi");
    } else if (deviceType === 3) {
      // For new video devices, also switch to Yingshi tab
      setActiveTabKey("yingshi");
    } else {
      setActiveTabKey("basic");
    }
  }, [initialValues.deviceType, editingDevice]);

  // Handle camera selection from Yingshi integration
  const handleCamerasSelected = (cameras: YingshiCamera[]) => {
    setSelectedCameras(cameras);
  };

  // Handle form submission
  const handleSubmit = () => {
    // Get the current form values
    const currentValues = form.getFieldsValue();
    
    // If this is a video device (type 3) and we have selected cameras
    if (currentValues.deviceType === 3 && selectedCameras.length > 0) {
      // Get the Yingshi appKey from the form
      const yingshiAppKey = form.getFieldValue('appKey');
      
      // Create clean submission values without any nested exData
      const cleanValues = { ...currentValues };
      // Remove both potential exData paths
      delete cleanValues.exData;
      delete cleanValues['exData.exData'];
      
      // Create the final values with the correct exData structure
      const values = {
        ...cleanValues,
        exData: {
          appKey: yingshiAppKey,
          channel: selectedCameras.map(camera => ({
            deviceSerial: camera.deviceSerial,
            channelNo: camera.channelNo,
            channelName: camera.channelName,
            status: camera.status
          }))
        }
      };
      
      // Log the values for debugging
      console.log('Submitting form with values:', values);
      
      // Call onFinish directly with the values
      onFinish(values);
      return;
    }
    
    // For non-video devices, ensure clean exData
    const cleanValues = { ...currentValues };
    if (cleanValues.exData?.exData) {
      cleanValues.exData = cleanValues.exData.exData;
    }
    onFinish(cleanValues);
  };

  // Handle device type change
  const handleDeviceTypeChange = (value: number) => {
    // Call the parent handler
    onDeviceTypeChange(value);
    
    // If changing to video type, switch to Yingshi tab
    if (value === 3) {
      setActiveTabKey("yingshi");
    } else {
      setActiveTabKey("basic");
    }
  };

  // Determine if the submit button should be disabled
  const isSubmitDisabled = () => {
    const deviceType = form.getFieldValue('deviceType');
    // Only disable if it's a video device, we're on the Yingshi tab, and no cameras are selected
    return deviceType === 3 && activeTabKey === "yingshi" && selectedCameras.length === 0;
  };

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          ...initialValues,
          exData: undefined
        }}
        requiredMark={false}
        preserve={false}
      >
        {/* Hidden form item for exData with correct initial value */}
        <Form.Item 
          name="exData" 
          initialValue={initialValues.exData?.exData || initialValues.exData || {}}
          hidden
        >
          <input />
        </Form.Item>

        <Tabs 
          activeKey={activeTabKey} 
          onChange={setActiveTabKey}
          items={[
            {
              key: "basic",
              label: "基本信息",
              children: (
                <FixedDeviceForm
                  form={form}
                  initialValues={initialValues}
                  formConfig={formConfig}
                  onDeviceTypeChange={handleDeviceTypeChange}
                />
              )
            },
            {
              key: "yingshi",
              label: (
                <Badge 
                  count={selectedCameras.length > 0 ? selectedCameras.length : 0} 
                  size="small" 
                  offset={[10, 0]}
                  style={{ backgroundColor: selectedCameras.length > 0 ? '#52c41a' : '#d9d9d9' }}
                >
                  萤石云集成
                </Badge>
              ),
              disabled: form.getFieldValue('deviceType') !== 3,
              children: (
                <YingshiIntegration
                  editingDevice={editingDevice}
                  onCamerasSelected={handleCamerasSelected}
                  form={form}
                />
              )
            }
          ]}
        />
        
        {/* Form Actions */}
        <div className="form-actions" style={{ marginTop: 24 }}>
          <Button onClick={onCancel}>取消</Button>
          <Button 
            type="primary" 
            onClick={handleSubmit}
            disabled={isSubmitDisabled()}
          >
            {isEditing ? "更新" : "创建"}
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DeviceForm; 